import { createContext, useContext, useState, useEffect } from "react";
import { getMenuConfig } from "../config/menuConfig";
import { AuthContext } from "./AuthContext";
import api, { getImagemMenuItemUrl } from "../services/api";

const CACHE_TTL = 15 * 60 * 1000;

// Cache "fresco" (dentro do TTL) - usado pra já pintar a tela na hora, sem
// esperar o fetch. Ignora e retorna null se passou do TTL.
function lerCache(codfun) {
    try {
        const raw = localStorage.getItem(`menuCfg_${codfun}`);
        if (!raw) return null;
        const { ts, data } = JSON.parse(raw);
        if (Date.now() - ts > CACHE_TTL) return null;
        return data;
    } catch { return null; }
}

// Cache de QUALQUER idade (ignora TTL) - o "último bom conhecido" vindo de
// verdade da API numa sessão anterior. Usado só como rede de segurança
// quando o fetch atual falha: é sempre melhor que o menuConfig.js estático
// (hardcoded no código, nunca reflete mudanças feitas no CozimaxERP depois
// do último build/deploy) - ver MenuProvider abaixo (pedido do usuário,
// 2026-10-26: "fica o menu antigo... aí abre coisas erradas").
function lerCacheAntigo(codfun) {
    try {
        const raw = localStorage.getItem(`menuCfg_${codfun}`);
        if (!raw) return null;
        const { data } = JSON.parse(raw);
        return data;
    } catch { return null; }
}

function salvarCache(codfun, rawSections) {
    try {
        localStorage.setItem(`menuCfg_${codfun}`, JSON.stringify({ ts: Date.now(), data: rawSections }));
    } catch {}
}

// Exportadas porque MenuConteudo.jsx precisa da mesma lógica pra resolver
// hotspots (botões clicáveis) - ver comentário lá.
export function resolveUrl(url, user) {
    if (!url) return url;
    return url
        .replace(/\{toksim\}/g, user?.toksim ?? '')
        .replace(/\{cpffun\}/g, user?.cpffun ?? '');
}

// ToJSONArray (FireDAC) devolve os campos em minúsculo, não no camelCase do
// alias da SQL - normaliza aqui uma vez em vez de checar as duas grafias
// em cada lugar que lê o campo.
export function lc(obj) {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));
}

function normalizar(rawSections, user) {
    return rawSections.map(raw => {
        const sec = lc(raw);
        return {
            id:         sec.idsec ?? sec.id,
            section:    sec.label ?? sec.labsec ?? sec.section,
            icon:       sec.icone ?? sec.icosec ?? sec.icon,
            shortLabel: sec.shortlabel ?? sec.shlsec ?? sec.shortLabel,
            nav:        !!(sec.nav ?? sec.navsec ?? 1),
            // Aparece no menu lateral (drawer) - separado do nav (menu
            // inferior), pedido do usuário, 2026-09-02. Default true
            // preserva o comportamento de sempre (drawer mostrava tudo).
            navLateral: !!(sec.navlateral ?? 1),
            items: (sec.itens ?? sec.items ?? []).map(rawItem => {
                const item    = lc(rawItem);
                const tipo    = item.tipo ?? item.tipite;
                const destino = item.destino ?? item.desite;
                const codite  = item.codite;
                let acao;
                if (tipo === 'rota') acao = { rota: destino, subtitulo: item.subtitulo };
                else if (tipo === 'conteudo') {
                    // Item tipo 'conteudo' não navega - abre uma tela simples com
                    // imagem/texto que já vêm junto no próprio menu (sem fetch novo,
                    // ver SectionSheet.jsx/MenuConteudo).
                    const conteudoTexto  = item.conteudotexto ?? item.conteudoTexto;
                    const temImagem      = !!(item.conteudoimagem ?? item.conteudoImagem);
                    acao = { conteudo: { codite, texto: conteudoTexto, imagemUrl: temImagem ? getImagemMenuItemUrl(codite) : null, subtitulo: item.subtitulo } };
                }
                else acao = { url: resolveUrl(destino, user) };
                return {
                    label: item.label ?? item.labite,
                    icon:  item.icone ?? item.icoite ?? item.icon,
                    ...acao,
                };
            }),
        };
    });
}

function valido(sections) {
    return Array.isArray(sections) && sections.length > 0 && !!sections[0].section;
}

export const MenuContext = createContext([]);

// Contexto separado só pro estado de erro/carregando/recarregar - MenuContext
// continua devolvendo o array puro (como sempre foi) pra não quebrar os
// vários lugares que já fazem useMenuConfig().filter(...) direto. Quem quiser
// mostrar loading/aviso consome esse aqui.
export const MenuStatusContext = createContext({ erro: false, carregando: false, recarregar: () => {} });

const MAX_TENTATIVAS = 3;

export function MenuProvider({ children }) {
    const { user } = useContext(AuthContext);
    const fallback = getMenuConfig(user);

    // Rastreia de onde vieram as seções atuais, pra saber se é seguro
    // sobrescrever silenciosamente com um fallback pior (nunca piora: api >
    // cache-antigo > estático). Estático só entra DEPOIS de confirmar que o
    // fetch falhou de verdade - nunca como "chute inicial" enquanto ainda
    // está carregando (pedido do usuário, 2026-10-26: "fica aparecendo o
    // menu que está aqui blindado [estático] até carregar... confunde as
    // pessoas" - mostrar menu errado SEM nenhum aviso, mesmo que só por
    // alguns segundos, é pior do que mostrar um loading).
    const origemRef = { atual: "nenhum" };

    const [sections, setSections] = useState(() => {
        if (!user?.codfun) return fallback;
        const cached = lerCache(user.codfun);
        if (cached) {
            const norm = normalizar(cached, user);
            if (valido(norm)) { origemRef.atual = "cache"; return norm; }
        }
        return [];
    });

    // Só fica "carregando" (pra mostrar skeleton em vez do menu) quando não
    // tinha nem cache fresco pra já pintar a tela na hora.
    const [carregando, setCarregando] = useState(origemRef.atual === "nenhum");
    const [erro, setErro] = useState(false);

    function buscar(tentativa = 1) {
        if (!user?.codfun) return;
        api.get(`/colaborador/menu/?codfun=${user.codfun}&tipfun=${user.tipfun}&_t=${Date.now()}`)
            .then(r => {
                const raw = Array.isArray(r.data) ? r.data : r.data?.sections;
                if (!Array.isArray(raw) || raw.length === 0) throw new Error("menu vazio");
                const norm = normalizar(raw, user);
                if (!valido(norm)) throw new Error("menu inválido");
                salvarCache(user.codfun, raw);
                origemRef.atual = "api";
                setSections(norm);
                setErro(false);
                setCarregando(false);
            })
            .catch(() => {
                // Falhou de verdade (rede caiu, API fora, etc). Se ainda não
                // tem nada na tela (nem cache fresco), tenta o último cache
                // REAL que essa API já devolveu antes (mesmo vencido) - é
                // sempre mais correto que o estático, que não acompanha
                // mudança nenhuma feita no CozimaxERP depois do deploy. Só
                // cai no estático se nem isso existir (primeiro acesso desse
                // aparelho sem nunca ter conseguido buscar o menu).
                if (origemRef.atual === "nenhum") {
                    const antigo = lerCacheAntigo(user.codfun);
                    if (antigo) {
                        const norm = normalizar(antigo, user);
                        if (valido(norm)) { origemRef.atual = "cache"; setSections(norm); }
                        else { origemRef.atual = "estatico"; setSections(fallback); }
                    } else {
                        origemRef.atual = "estatico";
                        setSections(fallback);
                    }
                }
                setErro(true);
                setCarregando(false);
                // Tenta de novo sozinho algumas vezes (rede instável no
                // momento do login é o caso mais comum) antes de desistir e
                // deixar só o aviso/botão manual de recarregar.
                if (tentativa < MAX_TENTATIVAS) {
                    setTimeout(() => buscar(tentativa + 1), tentativa * 4000);
                }
            });
    }

    useEffect(() => {
        buscar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.codfun]);

    // Reforça a tentativa quando o app volta pro primeiro plano (comum no
    // PWA: abriu sem rede, usuário sai e volta já com internet).
    useEffect(() => {
        function aoFicarVisivel() {
            if (document.visibilityState === "visible" && erro) buscar(1);
        }
        document.addEventListener("visibilitychange", aoFicarVisivel);
        return () => document.removeEventListener("visibilitychange", aoFicarVisivel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erro, user?.codfun]);

    return (
        <MenuContext.Provider value={sections}>
            <MenuStatusContext.Provider value={{ erro, carregando, recarregar: () => buscar(1) }}>
                {children}
            </MenuStatusContext.Provider>
        </MenuContext.Provider>
    );
}
